import { useMemo } from "react";
import type { PaymentTransaction } from "../../../domain/entities/PaymentTransaction";
import type { PaymentMethod } from "../../../domain/entities/PaymentMethod";
import type { Order } from "../../../domain/entities/Order";
import { checkoutCopy, checkoutLocale } from "../../i18n/checkout/es";
import { ReturnToAppButton } from "../../components/common/ReturnToAppButton";
import styles from "./PaymentStatusPage.module.css";

const formatDateTime = (date: Date, locale: string): string =>
  new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);

interface PaymentSuccessPageProps {
  transaction: PaymentTransaction;
  paymentMethodName: string;
  paymentMethod?: PaymentMethod | null;
  order: Order;
}

export const PaymentSuccessPage = ({ transaction, paymentMethodName, paymentMethod, order }: PaymentSuccessPageProps) => {
  const paidAt = useMemo(() => formatDateTime(transaction.updatedAt, checkoutLocale), [transaction.updatedAt]);
  const methodLabel = paymentMethod?.name ?? paymentMethodName ?? checkoutCopy.status.unknownMethodName;

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(checkoutLocale, {
        style: "currency",
        currency: order.amount.currency,
      }),
    [order.amount.currency],
  );

  return (
    <div className={styles.page} role="main">
      <section className={styles.card} aria-labelledby="payment-success-title">
        <div className={`${styles.statusIcon} ${styles.successIcon}`} aria-hidden="true">
          ✓
        </div>
        <header className={styles.statusHeader}>
          <h1 id="payment-success-title" className={styles.statusTitle}>
            {checkoutCopy.status.success.title}
          </h1>
          <p className={styles.statusDescription}>{checkoutCopy.status.success.description}</p>
        </header>

        <div className={styles.summarySection}>
          <h2 className={styles.summaryTitle}>{checkoutCopy.status.success.summaryTitle(order.code)}</h2>
          <dl className={styles.statusDetails}>
            <div className={styles.detailRow}>
              <dt className={styles.detailLabel}>{checkoutCopy.status.success.amountLabel}</dt>
              <dd className={styles.detailValue}>{currencyFormatter.format(transaction.amount)}</dd>
            </div>
            <div className={styles.detailRow}>
              <dt className={styles.detailLabel}>{checkoutCopy.status.success.referenceLabel}</dt>
              <dd className={styles.detailValue}>{transaction.referenceCode}</dd>
            </div>
            <div className={styles.detailRow}>
              <dt className={styles.detailLabel}>{checkoutCopy.status.success.methodLabel}</dt>
              <dd className={styles.detailValue}>{methodLabel}</dd>
            </div>
            <div className={styles.detailRow}>
              <dt className={styles.detailLabel}>{checkoutCopy.status.success.processedAtLabel}</dt>
              <dd className={styles.detailValue}>{paidAt}</dd>
            </div>
          </dl>
        </div>

        <div className={styles.actions}>
          <ReturnToAppButton
            label={checkoutCopy.status.return.action}
            bookingCode={order.bookingCode ?? order.code}
          />
          <p className={styles.returnHint}>{checkoutCopy.status.return.hint}</p>
        </div>
      </section>
    </div>
  );
};
